import { clsx } from 'clsx'

import {
  BannerPlaceholder,
  EntityImage,
} from '@tribeplatform/react-components/common/components'
import type { StaticField, View } from '@tribeplatform/react-components/Views'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import type { QuickLinksSource } from '../types.js'
import { getGlyphSize } from '../utils/index.js'

interface Props {
  field: StaticField
  source: QuickLinksSource
  highlighted: boolean
  mediaUrl: string
  aspectRatio: StaticField['settings']['aspectRatio']
  gallerySize: View['gallerySize']
}
export const GalleryBanner = ({
  field,
  source,
  highlighted,
  mediaUrl,
  aspectRatio,
  gallerySize,
}: Props) => {
  const fieldMediaUrl = mediaUrl
  const { bannerImage } = source
  const glyphSize = getGlyphSize(gallerySize)

  if (fieldMediaUrl) {
    switch (field.settings?.bannerStyle) {
      case 'banner':
        return (
          <div
            className={clsx(
              'w-full relative',
              aspectRatio || 'aspect-video',
              highlighted && 'border-2 border-focused',
            )}
          >
            <div
              className="w-full h-full bg-cover bg-center absolute top-0 start-0"
              style={{ backgroundImage: `url(${fieldMediaUrl})` }}
            ></div>
          </div>
        )
      case 'square':
        return (
          <div className="pb-4 relative">
            <Avatar src={fieldMediaUrl} size="4x" rounded="md" />
          </div>
        )
      default:
        // Code should never reach the default case
        // eslint-disable-next-line no-case-declarations
        const exhaustiveCheck: never = field.settings?.bannerStyle
        return exhaustiveCheck
    }
  }

  if (bannerImage) {
    switch (field.settings?.bannerStyle) {
      case 'banner':
        return (
          <div
            className={clsx(
              'w-full relative',
              aspectRatio || 'aspect-video',
              highlighted && 'border-2 border-focused',
            )}
          >
            <div className="w-full h-full flex justify-center items-center text-content-on-background">
              <EntityImage entity={{ image: bannerImage }} size={glyphSize} />
            </div>
          </div>
        )
      case 'square':
        return (
          <div className="pb-4 relative">
            {!fieldMediaUrl ? (
              <BannerPlaceholder
                className={clsx(
                  'h-16 w-16 flex justify-center items-center rounded-base',
                  'overflow-hidden',
                  highlighted && 'ring-2 ring-offset-surface ring-focused',
                )}
                name={source?.id}
              />
            ) : (
              <Avatar src={fieldMediaUrl} size="4x" rounded="md" />
            )}
          </div>
        )
      default:
        // Code should never reach the default case
        // eslint-disable-next-line no-case-declarations
        const exhaustiveCheck: never = field.settings?.bannerStyle
        return exhaustiveCheck
    }
  }

  switch (field.settings?.bannerStyle) {
    case 'banner':
      return (
        <div
          className={clsx(
            'w-full relative',
            aspectRatio || 'aspect-video',
            highlighted && 'border-2 border-focused',
          )}
        >
          <div className="absolute inset-0">
            <BannerPlaceholder name={source?.id} />
          </div>
        </div>
      )
    case 'square':
      return (
        <div className="pb-4 relative">
          <BannerPlaceholder
            className={clsx(
              'h-16 w-16 flex justify-center items-center rounded-base',
              'overflow-hidden',
              highlighted && 'ring-2 ring-offset-surface ring-focused',
            )}
            name={source?.id}
          />
        </div>
      )
    default:
      // Code should never reach the default case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: never = field.settings?.bannerStyle
      return exhaustiveCheck
  }
}
